import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, AlertTriangle, Info, Clock, RefreshCw, ExternalLink, ChevronDown, ChevronUp, Sparkles, Users, CheckCircle, ShieldAlert, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', icon: <AlertTriangle className="w-5 h-5 text-red-500" />, label: 'Krytyczna zmiana', badge: 'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-300' },
  important: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: <Info className="w-5 h-5 text-amber-500" />, label: 'Wazna zmiana', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-300' },
  info: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: <Scale className="w-5 h-5 text-blue-500" />, label: 'Informacja', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-300' },
};

export default function LawChangesPage() {
  const navigate = useNavigate();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [enriching, setEnriching] = useState({});

  useEffect(() => {
    loadChanges();
  }, []);

  const loadChanges = async () => {
    try {
      const token = localStorage.getItem('token');
      const ts = Date.now();
      const response = await axios.get(`${BACKEND_URL}/api/law-changes?_t=${ts}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        timeout: 30000
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setChanges(data);
      setLastUpdated(new Date());
      if (data.length === 0) {
        toast.info('Baza zmian jest pusta. Kliknij "Odswiez" aby pobrac aktualne zmiany.');
      }
    } catch (error) {
      console.error('Law changes load error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sesja wygasla. Zaloguj sie ponownie.');
      } else {
        toast.error('Blad ladowania zmian w przepisach. Sprobuj odswiezyc strone.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/law-changes/refresh`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        timeout: 180000
      });
      toast.success(response.data.message || 'Odswiezono zmiany');
      await loadChanges();
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Blad odswiezania zmian');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEnrich = async (changeId) => {
    setEnriching(prev => ({ ...prev, [changeId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/law-changes/${changeId}/enrich`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChanges(prev => prev.map(c => c.id === changeId ? response.data : c));
      setExpanded(prev => ({ ...prev, [changeId]: true }));
      toast.success('Opis wzbogacony przez AI');
    } catch (error) {
      toast.error('Nie udalo sie wzbogacic opisu');
    } finally {
      setEnriching(prev => ({ ...prev, [changeId]: false }));
    }
  };


  const groupByMonth = (items) => {
    // First sort all items by date descending (newest first)
    const sorted = [...items].sort((a, b) => {
      const dateA = a.effective_date || a.created_at?.split('T')[0] || '0000-00-00';
      const dateB = b.effective_date || b.created_at?.split('T')[0] || '0000-00-00';
      return dateB.localeCompare(dateA);
    });
    const groups = {};
    sorted.forEach(item => {
      const date = item.effective_date || item.created_at?.split('T')[0] || '';
      const month = date.substring(0, 7) || 'Brak daty';
      if (!groups[month]) groups[month] = [];
      groups[month].push(item);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const formatMonth = (key) => {
    if (key === 'Brak daty') return key;
    const [year, month] = key.split('-');
    const months = ['', 'Styczen', 'Luty', 'Marzec', 'Kwiecien', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpien', 'Wrzesien', 'Pazdziernik', 'Listopad', 'Grudzien'];
    return `${months[parseInt(month)] || month} ${year}`;
  };

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      <div className="border-b bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="gap-2 text-slate-500">
              <ArrowLeft className="w-4 h-4" /> Powrot
            </Button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                <Scale className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-slate-800 dark:text-white">Zmiany w Przepisach</h1>
                <p className="text-xs text-slate-400">
                  {changes.length} zmian w bazie
                  {lastUpdated && (
                    <span className="ml-2 text-emerald-600 dark:text-emerald-400">
                      · odświeżono: {lastUpdated.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <Button
            data-testid="refresh-law-changes-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="gap-2 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Skanowanie portali...' : 'Odswiez'}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-16">
            <Clock className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
            <p className="text-sm text-slate-400">Ladowanie zmian...</p>
          </div>
        ) : changes.length === 0 ? (
          <div className="text-center py-16">
            <Scale className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="font-bold text-slate-600 dark:text-slate-300 mb-2">Brak zmian do wyswietlenia</h3>
            <p className="text-sm text-slate-400 mb-6">Kliknij "Odswiez" aby przeskanowac portale prawne</p>
            <Button onClick={handleRefresh} disabled={refreshing} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Skanuj portale
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {groupByMonth(changes).map(([month, items]) => (
              <div key={month}>
                <h2 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  {formatMonth(month)}
                  <span className="text-xs font-normal text-slate-400">({items.length})</span>
                </h2>
                <div className="space-y-4">
                  {items.map((change, idx) => {
                    const style = SEVERITY_STYLES[change.severity] || SEVERITY_STYLES.info;
                    const isExpanded = expanded[change.id];
                    const hasDetails = change.who_affected || change.action_required || change.consequences;
                    const isEnriched = !!change.enriched_at;
                    return (
                      <div
                        key={change.id || idx}
                        data-testid={`law-change-${idx}`}
                        className={`${style.bg} border ${style.border} rounded-xl p-5 transition-all hover:shadow-md`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0">{style.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.badge}`}>{style.label}</span>
                              {change.category && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-medium">{change.category}</span>
                              )}
                              {change.effective_date && (
                                <span className="text-xs text-slate-500 dark:text-slate-400">od {change.effective_date}</span>
                              )}
                              {change.deadline && (
                                <span className="text-xs text-orange-600 dark:text-orange-400 font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Termin: {change.deadline}</span>
                              )}
                            </div>
                            <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 mb-2">{change.title}</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{change.description}</p>
                            
                            {/* Rozwijane szczegóły */}
                            {isExpanded && hasDetails && (
                              <div className="mt-4 space-y-3 animate-in fade-in duration-200">
                                {change.who_affected && (
                                  <div className="flex items-start gap-2.5 bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3.5">
                                    <Users className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <span className="text-xs font-bold text-violet-700 dark:text-violet-300 uppercase tracking-wide block mb-1">Kogo dotyczy</span>
                                      <p className="text-sm text-violet-600 dark:text-violet-300 leading-relaxed">{change.who_affected}</p>
                                    </div>
                                  </div>
                                )}
                                {change.action_required && (
                                  <div className="flex items-start gap-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3.5">
                                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide block mb-1">Wymagane dzialania</span>
                                      <p className="text-sm text-emerald-600 dark:text-emerald-300 leading-relaxed">{change.action_required}</p>
                                    </div>
                                  </div>
                                )}
                                {change.consequences && (
                                  <div className="flex items-start gap-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg p-3.5">
                                    <ShieldAlert className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <span className="text-xs font-bold text-red-700 dark:text-red-300 uppercase tracking-wide block mb-1">Konsekwencje</span>
                                      <p className="text-sm text-red-600 dark:text-red-300 leading-relaxed">{change.consequences}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {change.legal_basis && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 italic border-t border-slate-200/50 dark:border-slate-600/50 pt-2">
                                <Scale className="w-3 h-3 inline mr-1" />
                                Podstawa prawna: {change.legal_basis}
                              </p>
                            )}

                            {/* Przyciski */}
                            <div className="flex items-center gap-2 mt-3 pt-2 flex-wrap">
                              {hasDetails && (
                                <button
                                  onClick={() => toggleExpand(change.id)}
                                  data-testid={`expand-law-change-${idx}`}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center gap-1 transition-colors"
                                >
                                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  {isExpanded ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                                </button>
                              )}
                              {!isEnriched && !hasDetails && (
                                <button
                                  onClick={() => handleEnrich(change.id)}
                                  disabled={enriching[change.id]}
                                  data-testid={`enrich-law-change-${idx}`}
                                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
                                >
                                  {enriching[change.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                  {enriching[change.id] ? 'Analizuję...' : 'Rozbuduj opis AI'}
                                </button>
                              )}
                              {change.source_url && (
                                <a href={change.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1 ml-auto transition-colors">
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  {"Źródło"}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
